//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { createFragmentContainer } from "react-relay";
import { connect } from "react-redux";
// week-in-review-scrubber imports
import { IContainer, IProps, IState } from "./WeekInReviewScrubber.d";
import { weekInReviewScrubberFragments } from "./WeekInReviewScrubber.gql";
import * as s from "./WeekInReviewScrubber.styles";
import { HourLabels, Legend, ScrubberLabel, PlayPauseButton } from "./WeekInReviewLabels";
// other components
import { WeekInReviewSnapshot } from "@components/week-in-review-snapshot";
// util
import { generateRewindRenderData } from "@util/rewind-history.util";
import { TrackingEvents, track } from "@util/analytics.util";
// state
import { IReduxState } from "@reducers/root.reducer";
import { weekInReviewPaused } from "@state/local-storage";
// material-ui
import { Slider } from "@material-ui/core";

//////////////////
// COMPONENT
//////////////////
export class WeekInReviewScrubberCmpt extends React.PureComponent<IProps, IState> {
  private animationInterval: ReturnType<typeof setInterval>;

  // ~-~-~-~-~-~-~-
  // Setup
  // ~-~-~-~-~-~-~-
  constructor(props: IProps) {
    super(props);

    const state = this.getStateFromProps(props);
    this.state = state;

    if (state.animating) {
      this.animateScrubber();
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-

  private getStateFromProps(props: IProps, forceAnimate?: boolean): IState {
    // these cards are static, so we can calculate the snapshots & workinghours once
    const { snapshots, overallWorkingHours } = generateRewindRenderData(
      props.rewindHistory.snapshots,
    );
    // get maxIndex for scrubber based on how many frames we have
    const scrubberIndexMax = snapshots.length - 1;
    // check local storage for paused state
    const animating = forceAnimate || !this.hasBeenPaused();
    // if not animating, set start index to random value
    const scrubberIndex = animating
      ? 0
      : Math.floor(Math.random() * Math.floor(scrubberIndexMax + 1));

    return {
      snapshots,
      overallWorkingHours,
      animating,
      scrubberIndex,
      scrubberIndexMax,
    };
  }

  // this animates the scrubber back-and-forth once
  private animateScrubber = () => {
    this.animationInterval = setInterval(() => {
      // check if maxed out
      const atMax = this.state.scrubberIndex === this.state.scrubberIndexMax;
      // if at max, restart to 0
      const scrubberIndex = atMax ? 0 : this.state.scrubberIndex + 1;
      // finally, set state
      this.setState({ scrubberIndex });
    }, 1250);
  };

  private clearAnimationInterval = () => {
    clearInterval(this.animationInterval);
  };

  // do the things needed to pause
  private pauseAnimation = () => {
    this.clearAnimationInterval();
    this.setState({ animating: false });
    weekInReviewPaused.set(this.props.rewindWeek);
    track(TrackingEvents.WEEK_IN_REVIEW.PAUSE);
  };

  // do the things needed to play
  private playAnimation = () => {
    this.animateScrubber();
    this.setState({ animating: true });
    track(TrackingEvents.WEEK_IN_REVIEW.PLAY);
  };

  // cleanup the interval on unmount
  public componentWillUnmount() {
    this.clearAnimationInterval();
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (nextProps.rewindHistory.id !== this.props.rewindHistory.id) {
      const state = this.getStateFromProps(nextProps, true);
      this.setState(state);
    }
  }

  // check local storage for animation state
  private hasBeenPaused = () => {
    const pauseDate = weekInReviewPaused.get();
    return !!pauseDate && pauseDate === this.props.rewindWeek;
  };

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  // when the user drags the scrubber manually, clear the animation
  private onDragStart = () => {
    if (this.state.animating) {
      this.pauseAnimation();
    }
    track(TrackingEvents.WEEK_IN_REVIEW.DRAG);
  };

  // when the scrubber moves, update the current index
  private onScrub = (_e: React.MouseEvent<{}>, value: number) => {
    const scrubberIndex = Math.round(value);
    this.setState({ scrubberIndex });
  };

  // toggle animation on/off
  private onClickPlayPause = () => {
    if (this.state.animating) {
      this.pauseAnimation();
    } else {
      this.playAnimation();
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  public render() {
    const {
      animating,
      scrubberIndex,
      snapshots,
      overallWorkingHours,
      scrubberIndexMax,
    } = this.state;
    const currentSnapshot = snapshots[scrubberIndex];

    return (
      <div style={s.container}>
        <Legend />

        <div style={s.weekContainer}>
          <HourLabels overallWorkingHours={overallWorkingHours} />
          <WeekInReviewSnapshot weekOfSlots={currentSnapshot} />
        </div>
        <div style={s.controlsContainer}>
          <PlayPauseButton onClick={this.onClickPlayPause} isAnimating={animating} />
          <div style={{ flexGrow: 1 }}>
            <Slider
              onChange={this.onScrub}
              onDragStart={this.onDragStart}
              value={scrubberIndex}
              max={scrubberIndexMax}
              min={0}
            />
            <ScrubberLabel weekSnapshotIndex={scrubberIndex} />
          </div>
        </div>
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
const mapStateToProps = (state: IReduxState, _ownProps: IContainer) => ({
  chromeExtensionIdle: state.webExtension.webExtensionIdle,
});

const WeekInReviewScrubber_redux = connect(mapStateToProps, {})(WeekInReviewScrubberCmpt);

//////////////////
// RELAY
//////////////////
export const WeekInReviewScrubber = createFragmentContainer<IContainer>(
  WeekInReviewScrubber_redux,
  weekInReviewScrubberFragments,
);
